import moment from 'moment';

export enum SortStrategy {
  DEFAULT,
  NUMBER,
  DATE,
}

export const sortBasedOnStrategy = <T extends object>(
  sortStrategy: SortStrategy,
  tableRows: T[],
  sortColumn: keyof T,
  sortDirection: string,
) => {
  switch (sortStrategy) {
    case SortStrategy.DEFAULT:
      return defaultStrategy(tableRows, sortColumn, sortDirection);
    case SortStrategy.DATE:
      return dateStrategy(tableRows, sortColumn, sortDirection);
    case SortStrategy.NUMBER:
      return numberStrategy(tableRows, sortColumn, sortDirection);
    default:
      return defaultStrategy(tableRows, sortColumn, sortDirection);
  }
};

const defaultStrategy = <T extends object>(tableRows: T[], sortColumn: keyof T, sortDirection: string) => {
  return tableRows.sort((a: T, b: T) => {
    const aValue = a[sortColumn]?.toString().toLowerCase() || '';
    const bValue = b[sortColumn]?.toString().toLowerCase() || '';

    if (aValue === bValue) {
      return 0;
    }

    // Null or undefined values are positioned
    // at the start when sorted descending
    // and at the end when sorted ascending
    if (!aValue) {
      return sortDirection === 'ascending' ? 1 : -1;
    }
    if (!bValue) {
      return sortDirection === 'ascending' ? -1 : 1;
    }

    return sortDirection === 'ascending' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
  });
};

const numberStrategy = <T extends object>(tableRows: T[], sortColumn: keyof T, sortDirection: string) => {
  return tableRows.sort((a: any, b: any) => {
    const aValue = +a[sortColumn];
    const bValue = +b[sortColumn];

    if (aValue < bValue) {
      return sortDirection === 'ascending' ? -1 : 1;
    } else if (aValue > bValue) {
      return sortDirection === 'ascending' ? 1 : -1;
    }

    return 0;
  });
};

const dateStrategy = <T extends object>(tableRows: T[], sortColumn: keyof T, sortDirection: string) => {
  return tableRows?.sort((a: any, b: any) => {
    const aValue = moment(a[sortColumn]);
    const bValue = moment(b[sortColumn]);

    if (aValue.isBefore(bValue)) {
      return sortDirection === 'ascending' ? -1 : 1;
    } else if (aValue.isAfter(bValue)) {
      return sortDirection === 'ascending' ? 1 : -1;
    }

    return 0;
  });
};
